import { SystemTags } from '../db/constants';
import { Page } from '../db/pages';
import { stem } from '../shared/stemmer';

const ignoreTags = new Set(SystemTags);

export function cosineSimilarity(a: number[], b: number[]): number {
    if (!a || !b || a.length !== b.length) return 0;
    
    let dotProduct = 0;
    let normA = 0;
    let normB = 0;
    
    for (let i = 0; i < a.length; i++) {
        dotProduct += a[i] * b[i];
        normA += a[i] * a[i];
        normB += b[i] * b[i];
    }
    
    if (normA === 0 || normB === 0) return 0;
    return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

// EncodedPage stores pre-computed values for faster similarity comparison
export interface EncodedPage {
    page: Page;
    cleanTitleNgrams: Set<string>;
    stemmedTitleNgrams: Set<string>;
    cleanUrlNgrams: Set<string>;
    humanTagsSet: Set<string>;
}

/*
export function encodePage(page: Page): EncodedPage {
    const cleanTitle = removeIgnoreWords(page.title);
    const cleanUrl = removeIgnoreWords(page.nurl.normalized);
    
    // Stemming
    const words = cleanTitle?.split(/\s+/) || [];
    const stemmedTitle = words.map(w => stem(w.toLowerCase())).join(' ');

    return {
        page,
        cleanTitleNgrams: getNgrams(cleanTitle, 3),
        stemmedTitleNgrams: getNgrams(stemmedTitle, 3),
        cleanUrlNgrams: getNgrams(cleanUrl, 3),
        humanTagsSet: new Set(page.humanTags),
    };
}

export function computeEncodedPageSimilarity(focal: EncodedPage, other: EncodedPage): number {
    if (focal.page.nurl.normalized == other.page.nurl.normalized) {
        return 1;
    }

    const titleSimilarity = setSimilarity(focal.cleanTitleNgrams, other.cleanTitleNgrams);
    const stemmedTitleSimilarity = setSimilarity(focal.stemmedTitleNgrams, other.stemmedTitleNgrams);
    const urlSimilarity = setSimilarity(focal.cleanUrlNgrams, other.cleanUrlNgrams);

    const accessSimilarity = focal.page.timeAdjacentUrls.includes(other.page.nurl.normalized) ? 1 : 0;

    const openerSimilarity =
        focal.page.openedUrls.includes(other.page.nurl.normalized) ||
        focal.page.openerUrls.includes(other.page.nurl.normalized)
            ? 1
            : 0;

    // TODO faster list intersection?? -> Managed by Set intersection now
    const tagsInCommon = focal.humanTagsSet
        .intersection(other.humanTagsSet)
        .difference(ignoreTags).size;
        
    const tagSimilarityDenominator = Math.max(
        focal.page.humanTags.length + other.page.humanTags.length - tagsInCommon,
        1
    );
    const tagSimilarity = tagsInCommon / tagSimilarityDenominator;

    return (
        0.15 * titleSimilarity +
        0.15 * stemmedTitleSimilarity +
        0.1 * urlSimilarity +
        0.1 * accessSimilarity +
        0.3 * openerSimilarity +
        0.2 * tagSimilarity
    );
}

function setSimilarity(a: Set<string>, b: Set<string>): number {
    if (a.size === 0 && b.size === 0) return 0; // Avoid NaN if both empty
    if (a.size === 0 || b.size === 0) return 0; 
    
    const intersectionSize = a.intersection(b).size;
    const unionSize = a.size + b.size - intersectionSize;
    return intersectionSize / Math.max(unionSize, 1);
}

function getNgrams(text: string | undefined, n: number): Set<string> {
   if (!text) return new Set();
   const ngrams = new Set<string>();
   for (let i = 0; i + n <= text.length; i++) {
       ngrams.add(text.slice(i, i + n));
   }
   return ngrams;
}

export function computePageSimilarity(focalPage: Page, otherPage: Page): number {
  return computeEncodedPageSimilarity(encodePage(focalPage), encodePage(otherPage));
}
  */

export function getIgnoreWords() {
  return ['Google', 'Search', 'YouTube', 'Docs', 'Sheets'];
}


function removeIgnoreWords(text?: string) {
  if (!text) return '';
  const regex = new RegExp(`\\b(${getIgnoreWords().join('|')})\\b`, 'gi');
  return text.replace(regex, '').trim();
}

// Deprecated in favor of setSimilarity with pre-computed sets, kept if needed for backward compact but computePageSimilarity now uses the new way
function ngramSimilarity(a?: string, b?: string, n?: number) {
  if (a == null || b == null || n == null) return 0;
  const aNgrams = new Set();
  for (var aTrav = 0; aTrav + n < a.length; aTrav++) {
    aNgrams.add(a.slice(aTrav, aTrav + n));
  }
  const bNgrams = new Set<string>();
  for (var bTrav = 0; bTrav + n < b.length; bTrav++) {
    bNgrams.add(b.slice(bTrav, bTrav + n));
  }

  // denominator
  const denom = Math.max(aNgrams.size + bNgrams.size - aNgrams.intersection(bNgrams).size, 1);

  return aNgrams.intersection(bNgrams).size / denom;
}
